import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import { getRideEvents } from "api/rideApi";
import { unwrapList } from "api/unwrap";

/**
 * Audit-trail history for a single ride. Shows up to 200 events newest-first.
 * Backed by GET /api/v1/rides/{rideId}/events. Collapsed by default — the
 * common case is "ride opened, status looks normal, no need to dig." Expand
 * when triaging "when did this change / who did it?"
 */

const ACTION_COLORS = {
  ASSIGN: "#0ea5e9",
  UNASSIGN: "#dc2626",
  ACCEPT: "#16a34a",
  DECLINE: "#dc2626",
  CANCEL: "#dc2626",
  STATUS_CHANGE: "#a855f7",
  CREATED: "#16a34a",
  COMPLETED: "#16a34a",
};

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

export const RideEventsPanel = ({ rideId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!rideId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getRideEvents(rideId);
      setEvents(unwrapList(res));
      setLoaded(true);
    } catch (e) {
      setError(e?.message || "Failed to load ride history");
    } finally {
      setLoading(false);
    }
  }, [rideId]);

  // Lazy-load when expanded — avoids hammering the endpoint when every ride
  // detail view auto-fires the query on mount.
  const onExpand = (_e, expanded) => {
    if (expanded && !loaded && !loading) load();
  };

  // If rideId changes (admin opens a different ride), invalidate cache.
  useEffect(() => {
    setEvents([]);
    setLoaded(false);
  }, [rideId]);

  return (
    <Accordion onChange={onExpand} sx={{ borderRadius: "12px", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <HistoryIcon sx={{ mr: 1, color: "#64748b" }} />
        <Typography sx={{ fontWeight: 600 }}>Ride history</Typography>
        {loaded ? (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, alignSelf: "center" }}>
            ({events.length} event{events.length === 1 ? "" : "s"})
          </Typography>
        ) : null}
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">{error}</Typography>
        ) : events.length === 0 && loaded ? (
          <Typography variant="body2" color="text.secondary">
            No events recorded for this ride.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {events.map((e) => (
              <Box
                key={e.id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  borderLeft: `3px solid ${ACTION_COLORS[e.action] || "#64748b"}`,
                  pl: 1.5,
                  py: 0.5,
                }}
              >
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {fmt(e.createdAtMs)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    <Chip
                      label={e.action}
                      size="small"
                      sx={{
                        backgroundColor: ACTION_COLORS[e.action] || "#64748b",
                        color: "#fff",
                        fontWeight: 600,
                        height: 20,
                        fontSize: "0.7rem",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {e.author || "system"}
                    </Typography>
                  </Box>
                  {e.details ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {e.details}
                    </Typography>
                  ) : null}
                  {e.comment ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, fontStyle: "italic" }}>
                      {e.comment}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default RideEventsPanel;
